import {
  ContentFeatureModel,
  DisclaimerFeatureModel,
  GetQsgSearchResultsResponse,
  IconlistFeatureModel,
  MediaFeatureModel,
  QsgSearchResult,
  QsgV1Model,
  SectionChainItem,
  SectionModel,
} from '../../fetch-data/graphql';
import {
  searchInContentItems,
  searchInDisclaimerFeatures,
  searchInFeatureTitles,
  searchInIconListFeatures,
  searchInMarkdownTitles,
  searchInMediaFeatures,
} from './feature';
import { extractPreviewFromFeatures } from './extract';
import { matchesQueryFuzzy } from './fuzzy';
import {
  createSectionChainItem,
  isContentFeature,
  isDisclaimerFeature,
  isIconListFeature,
  isMediaFeature,
  normalizeSearchTerm,
} from './utils';
import { findMatchingIntent, getFeatureKeywordsFromIntent } from './intent';

function createSubsectionResults(
  subsections: SectionModel[],
  parentSection: SectionModel,
  newChain: Array<SectionChainItem>,
  allResults: QsgSearchResult[],
  searchTerm?: string,
  useFuzzy: boolean = false
): void {
  for (const subsection of subsections) {
    if (!subsection) continue;

    const preview = extractPreviewFromFeatures(subsection.features);

    if (useFuzzy && searchTerm) {
      const previewText =
        preview.previewContent || preview.previewPlaintext || '';
      if (previewText && !matchesQueryFuzzy(previewText, searchTerm)) {
        continue;
      }
    }

    const subsectionTitle = subsection.sectionTitle?.plaintext?.trim() || '';
    const subsectionTitleHtml = subsection.sectionTitle?.html || null;

    const subsectionChainItem = {
      sectionId: subsection.sectionId || '',
      sectionName: parentSection.sectionName || '',
      sectionTitle: {
        html: subsection.sectionTitle?.html || null,
        plaintext: subsection.sectionTitle?.plaintext || null,
      },
    };
    const subsectionChain = [...newChain, subsectionChainItem];

    allResults.push({
      chain: subsectionChain,
      sectionId: subsection.sectionId || '',
      sectionName: subsection.sectionName || '',
      sectionTitle: {
        html: subsection.sectionTitle?.html || null,
        plaintext: subsection.sectionTitle?.plaintext || null,
      },
      resultTitle: {
        html: subsectionTitleHtml,
        plaintext: subsectionTitle || subsection.sectionId || '',
      },
      resultContent: {
        html: preview.previewHtml,
        plaintext: preview.previewPlaintext,
        trimmed: preview.previewContent,
      },
    });
  }
}

function createSectionTitleMatchResult(
  section: any,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  allResults: QsgSearchResult[]
): void {
  const preview = extractPreviewFromFeatures(section.features);

  allResults.push({
    chain: newChain,
    sectionId: section.sectionId || '',
    sectionName: section.sectionName || '',
    sectionTitle: sectionChainItem.sectionTitle,
    resultTitle: {
      html: section.sectionTitle.html || null,
      plaintext: section.sectionTitle.plaintext.trim(),
    },
    resultContent: {
      html: preview.previewHtml,
      plaintext: preview.previewPlaintext,
      trimmed: preview.previewContent,
    },
  });
}

function handleSectionTitleMatch(
  section: SectionModel,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  lowerSearchTerm: string,
  searchTerm: string,
  allResults: QsgSearchResult[],
  useFuzzy: boolean = false
): void {
  const sectionTitleText = section.sectionTitle?.plaintext || '';

  const matches = useFuzzy
    ? matchesQueryFuzzy(sectionTitleText, searchTerm)
    : sectionTitleText.toLowerCase().includes(lowerSearchTerm);

  if (!matches) return;

  const hasContentFeatures = section.features?.some((f) => isContentFeature(f));
  const hasSubsections = section.subsections && section.subsections.length > 0;

  if (!hasContentFeatures && hasSubsections) {
    createSubsectionResults(
      section.subsections || [],
      section,
      newChain,
      allResults,
      searchTerm,
      useFuzzy
    );
  } else {
    if (useFuzzy) {
      const preview = extractPreviewFromFeatures(section.features);
      const previewText =
        preview.previewContent || preview.previewPlaintext || '';

      if (previewText && !matchesQueryFuzzy(previewText, searchTerm)) {
        return;
      }
    }

    createSectionTitleMatchResult(
      section,
      sectionChainItem,
      newChain,
      allResults
    );
  }
}

function searchInFeatures(
  section: SectionModel,
  lowerSearchTerm: string,
  searchTerm: string,
  sectionChainItem: SectionChainItem,
  newChain: Array<SectionChainItem>,
  allResults: QsgSearchResult[],
  useFuzzy: boolean = false
): void {
  if (!section.features) return;

  const allFeatures = section.features;

  for (
    let featureIndex = 0;
    featureIndex < allFeatures.length;
    featureIndex++
  ) {
    const feature = allFeatures[featureIndex];
    if (!feature) continue;

    if (isContentFeature(feature)) {
      const matchedContentIndices = new Set<number>();

      searchInFeatureTitles(
        feature as ContentFeatureModel,
        lowerSearchTerm,
        searchTerm,
        section,
        sectionChainItem,
        newChain,
        allResults,
        useFuzzy
      );

      searchInMarkdownTitles(
        feature as ContentFeatureModel,
        searchTerm,
        section,
        sectionChainItem,
        newChain,
        matchedContentIndices,
        allResults,
        useFuzzy
      );

      searchInContentItems(
        feature as ContentFeatureModel,
        lowerSearchTerm,
        searchTerm,
        section,
        sectionChainItem,
        newChain,
        new Set<number>(), // Don't skip content items - search all of them to find all matches
        allResults,
        useFuzzy
      );
    } else if (isDisclaimerFeature(feature)) {
      searchInDisclaimerFeatures(
        feature as DisclaimerFeatureModel,
        lowerSearchTerm,
        searchTerm,
        featureIndex,
        allFeatures,
        section,
        sectionChainItem,
        newChain,
        allResults,
        useFuzzy
      );
    } else if (isIconListFeature(feature)) {
      searchInIconListFeatures(
        feature as IconlistFeatureModel,
        lowerSearchTerm,
        searchTerm,
        featureIndex,
        allFeatures,
        section,
        sectionChainItem,
        newChain,
        allResults,
        useFuzzy
      );
    } else if (isMediaFeature(feature)) {
      searchInMediaFeatures(
        feature as MediaFeatureModel,
        lowerSearchTerm,
        searchTerm,
        featureIndex,
        allFeatures,
        section,
        sectionChainItem,
        newChain,
        allResults,
        useFuzzy
      );
    }
  }
}

export function searchInSections(
  sections: SectionModel[] | null | undefined,
  searchTerm: string,
  currentChain: Array<SectionChainItem>,
  allResults: QsgSearchResult[],
  useFuzzy: boolean = false
): void {
  if (!sections) return;

  const lowerSearchTerm = normalizeSearchTerm(searchTerm);

  for (const section of sections) {
    if (!section) continue;

    const sectionChainItem = createSectionChainItem(section);
    const newChain = [...currentChain, sectionChainItem];

    handleSectionTitleMatch(
      section,
      sectionChainItem,
      newChain,
      lowerSearchTerm,
      searchTerm,
      allResults,
      useFuzzy
    );

    searchInFeatures(
      section,
      lowerSearchTerm,
      searchTerm,
      sectionChainItem,
      newChain,
      allResults,
      useFuzzy
    );

    if (section.subsections) {
      searchInSections(
        section.subsections,
        searchTerm,
        newChain,
        allResults,
        useFuzzy
      );
    }
  }
}

function isTitleMatch(
  result: QsgSearchResult,
  searchTerm: string,
  useFuzzy: boolean
): boolean {
  const titleText = result.resultTitle?.plaintext || '';
  if (!titleText) return false;

  const lowerSearchTerm = normalizeSearchTerm(searchTerm);

  if (useFuzzy) {
    return matchesQueryFuzzy(titleText, searchTerm);
  } else {
    return titleText.toLowerCase().includes(lowerSearchTerm);
  }
}

/**
 * Calculate a comprehensive score for a search result based on how well it matches the search term.
 * Considers:
 * - Exact phrase matches (highest score)
 * - Number of words matched
 * - Position of matches (title vs content)
 * - Word order and proximity
 */
function calculateResultScore(
  result: QsgSearchResult,
  searchTerm: string,
  useFuzzy: boolean
): number {
  const titleText = result.resultTitle?.plaintext || '';
  const contentText = result.resultContent?.plaintext || '';
  const sectionTitleText = result.sectionTitle?.plaintext || '';
  
  const lowerSearchTerm = normalizeSearchTerm(searchTerm);
  const searchWords = lowerSearchTerm.split(/\s+/).filter(w => w.length > 0);
  
  if (searchWords.length === 0) return 0;

  let score = 0;
  const lowerTitle = titleText.toLowerCase().trim();
  const lowerContent = contentText.toLowerCase().trim();
  const lowerSectionTitle = sectionTitleText.toLowerCase().trim();

  // Check if result matches at all
  const titleMatches = useFuzzy
    ? matchesQueryFuzzy(titleText, searchTerm)
    : lowerTitle.includes(lowerSearchTerm);
  const contentMatches = useFuzzy
    ? matchesQueryFuzzy(contentText, searchTerm)
    : lowerContent.includes(lowerSearchTerm);

  if (!titleMatches && !contentMatches) return 0;

  // Exact phrase match in title (highest priority)
  if (lowerTitle === lowerSearchTerm) {
    score += 1000;
  } else if (lowerTitle.startsWith(lowerSearchTerm)) {
    score += 800;
  } else {
    // Check for exact phrase match with word boundaries in title
    const escapedSearch = lowerSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const phraseRegex = new RegExp(`\\b${escapedSearch}\\b`, 'i');
    if (phraseRegex.test(titleText)) {
      score += 700;
    } else if (lowerTitle.includes(lowerSearchTerm)) {
      score += 500;
    }
  }

  // Count how many words from search term are found in title
  let titleWordMatches = 0;
  for (const word of searchWords) {
    const wordRegex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (wordRegex.test(titleText)) {
      titleWordMatches++;
    } else if (lowerTitle.includes(word)) {
      titleWordMatches += 0.5; // Partial match
    }
  }
  
  // Bonus for matching all words in title
  if (titleWordMatches === searchWords.length) {
    score += 400;
  } else if (titleWordMatches > 0) {
    score += titleWordMatches * 50; // Proportional score for partial matches
  }

  // Exact phrase match in content
  if (lowerContent.includes(lowerSearchTerm)) {
    const escapedSearch = lowerSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const phraseRegex = new RegExp(`\\b${escapedSearch}\\b`, 'i');
    if (phraseRegex.test(contentText)) {
      score += 300; // Exact phrase in content
    } else {
      score += 150; // Substring match in content
    }
  }

  // Count how many words from search term are found in content
  let contentWordMatches = 0;
  for (const word of searchWords) {
    const wordRegex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    if (wordRegex.test(contentText)) {
      contentWordMatches++;
    } else if (lowerContent.includes(word)) {
      contentWordMatches += 0.5; // Partial match
    }
  }
  
  // Bonus for matching all words in content
  if (contentWordMatches === searchWords.length && titleWordMatches === 0) {
    score += 200; // All words in content but not in title
  } else if (contentWordMatches > 0) {
    score += contentWordMatches * 20; // Proportional score
  }

  // Section title match bonus
  if (lowerSectionTitle.includes(lowerSearchTerm)) {
    score += 100;
  }

  // Fuzzy match penalty (lower score for fuzzy matches)
  if (useFuzzy && !titleMatches && !contentMatches) {
    score *= 0.3;
  }

  return score;
}


function resultMatchesFeatureKeywords(
  result: QsgSearchResult,
  featureKeywords: string[]
): boolean {
  if (!featureKeywords || featureKeywords.length === 0) return false;

  const normalizedText = (
    (result.resultTitle?.plaintext || '') +
    ' ' +
    (result.resultContent?.plaintext || '') +
    ' ' +
    (result.sectionTitle?.plaintext || '')
  ).toLowerCase();

  for (const keyword of featureKeywords) {
    const normalizedKeyword = keyword.toLowerCase();
    if (normalizedText.includes(normalizedKeyword)) {
      return true;
    }
  }

  return false;
}

function hasExactPhraseInContent(
  result: QsgSearchResult,
  searchTerm: string
): boolean {
  const normalizedSearch = normalizeSearchTerm(searchTerm).toLowerCase();
  const searchWords = normalizedSearch.split(/\s+/).filter((w) => w.length > 0);

  // Only check for exact phrase if search term has 2+ words
  if (searchWords.length < 2) return false;

  const contentText = (result.resultContent?.plaintext || '')
    .toLowerCase()
    .replace(/\n/g, ' ')
    .replace(/\s+/g, ' ')
    // Remove superscript numbers attached to words (e.g., "Locator5" -> "Locator")
    .replace(/([a-zA-Z])(\d+)(?=\s|$|[.,;:!?\-–—])/g, '$1')
    // Remove numbers at the start of words (e.g., "5Year" -> "Year")
    .replace(/(\s|^)(\d+)([a-zA-Z])/g, '$1$3');

  const escapedSearch = normalizedSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const phraseRegex = new RegExp(`\\b${escapedSearch}\\b`, 'i');

  return phraseRegex.test(contentText);
}

function sortResultsByPriority(
  results: QsgSearchResult[],
  searchTerm: string,
  useFuzzy: boolean,
  featureKeywords?: string[]
): QsgSearchResult[] {
  // First, calculate scores for all results
  const scoredResults = results.map(result => ({
    result,
    score: calculateResultScore(result, searchTerm, useFuzzy),
    matchesFeatureKeywords: featureKeywords && featureKeywords.length > 0
      ? resultMatchesFeatureKeywords(result, featureKeywords)
      : false,
    hasExactPhrase: hasExactPhraseInContent(result, searchTerm),
    isTitleMatch: isTitleMatch(result, searchTerm, useFuzzy),
  }));

  // Sort by priority
  return scoredResults.sort((a, b) => {
    // Priority 1: Feature keywords match
    if (a.matchesFeatureKeywords && !b.matchesFeatureKeywords) return -1;
    if (!a.matchesFeatureKeywords && b.matchesFeatureKeywords) return 1;

    // Priority 2: Exact phrase matches in content
    if (a.hasExactPhrase && !b.hasExactPhrase) return -1;
    if (!a.hasExactPhrase && b.hasExactPhrase) return 1;

    // Priority 3: Title matches
    if (a.isTitleMatch && !b.isTitleMatch) return -1;
    if (!a.isTitleMatch && b.isTitleMatch) return 1;

    // Priority 4: Overall score (comprehensive scoring)
    if (b.score !== a.score) {
      return b.score - a.score;
    }

    return 0;
  }).map(item => item.result);
}

/**
 * Deduplicate search results, keeping only the best result per section.
 * Results should be pre-sorted by score before calling this function.
 */
export function deduplicateSearchResults<
  T extends {
    sectionId?: string | null;
    resultTitle?: { plaintext?: string | null } | null;
  },
>(results: T[]): T[] {
  const seen = new Map<string, T>();
  
  for (const result of results) {
    const sectionId = result.sectionId || '';
    const sectionTitlePlaintext = result.resultTitle?.plaintext?.trim() || '';
    const key = `${sectionId}|||${sectionTitlePlaintext}`;

    // If we haven't seen this section+title combination, keep it
    // Since results are pre-sorted by score, the first one we encounter is the best
    if (!seen.has(key)) {
      seen.set(key, result);
    }
  }

  return Array.from(seen.values());
}

export const searchAndPaginateResults = (
  guide: QsgV1Model,
  searchTerm: string,
  page: number,
  limit: number
): GetQsgSearchResultsResponse => {
  let allResults: QsgSearchResult[] = [];
  let useFuzzy = false;
  let featureKeywords: string[] | undefined = undefined;

  const intentMatch = findMatchingIntent(searchTerm);
  if (intentMatch) {
    featureKeywords = getFeatureKeywordsFromIntent(intentMatch.intent);
  }

  if (guide && guide.sections) {
    searchInSections(guide.sections, searchTerm, [], allResults, true);
    useFuzzy = true;

    if (allResults.length === 0) {
      searchInSections(guide.sections, searchTerm, [], allResults, false);
      useFuzzy = false;
    }
  }

  const sortedResults = sortResultsByPriority(
    allResults,
    searchTerm,
    useFuzzy,
    featureKeywords
  );

  const uniqueResults = deduplicateSearchResults(sortedResults);

  const totalCount = uniqueResults.length;
  const pageNumber = Math.max(1, page || 1);
  const limitNumber = Math.max(1, limit || 10);
  const startIndex = (pageNumber - 1) * limitNumber;
  const endIndex = startIndex + limitNumber;
  const paginatedResults = uniqueResults.slice(startIndex, endIndex);
  const hasMore = endIndex < totalCount;

  return {
    results: paginatedResults,
    totalCount,
    page: pageNumber,
    limit: limitNumber,
    hasMore,
  };
};
